import React, { useContext } from "react"
import dynamic from "next/dynamic"

import { Alert, Button } from "@coval/ui"
import { BarChart3, ListChecks, TrendingUp } from "lucide-react"

import {
  chartSeriesColors,
  lineSeriesColors,
} from "@/components/charts/seriesPalette"
import { BUCKET_INTERVAL } from "@/utils/date/timeBucketing"

import { SeriesIsolationContext } from "../../contexts/SeriesIsolationContext"
import { SeriesVisibilityContext } from "../../contexts/SeriesVisibilityContext"
import { useChartWidgetData } from "../../hooks/chart/useChartWidgetData"
import type {
  ChartClickData,
  ChartWidgetConfig,
  WidgetDimensions,
} from "../../types/chart"
import {
  AGGREGATION_TYPE,
  DATA_SOURCE,
  VISUALIZATION_TYPE,
} from "../../types/core"
import { buildChartLevelDrillDownClickData } from "../../utils/chart/chartLevelDrillDown"
import { getDashboardWidgetChartSyncId } from "../../utils/chart/chartSync"
import {
  parseMetadataGroupKey,
  parseSplitKey,
} from "../../utils/chart/splitKeys"
import {
  isolateSeries,
  toggleHiddenSeries,
} from "../../utils/chart/visibilityUtils"
import {
  getResponsiveContainerWidth,
  getScaledDimensions,
  GRID_COLUMNS,
} from "../../utils/gridMetrics"
import {
  BaseWidget,
  type WidgetExportData,
  type WidgetProps,
} from "../BaseWidget"
import { ChartWidgetSkeleton } from "../ChartWidgetSkeleton"
import { WidgetNoDataState } from "../WidgetNoDataState"
import { ChartWidgetFigure } from "./ChartWidgetFigure"

const LineChartVisualization = dynamic(
  () =>
    import("./visualizations/LineChartVisualization").then(
      (mod) => mod.LineChartVisualization,
    ),
  { ssr: false },
)

const BarChartVisualization = dynamic(
  () =>
    import("./visualizations/BarChartVisualization").then(
      (mod) => mod.BarChartVisualization,
    ),
  { ssr: false },
)

const AreaChartVisualization = dynamic(
  () =>
    import("./visualizations/AreaChartVisualization").then(
      (mod) => mod.AreaChartVisualization,
    ),
  { ssr: false },
)

const StatisticVisualization = dynamic(
  () =>
    import("./visualizations/StatisticVisualization").then(
      (mod) => mod.StatisticVisualization,
    ),
  { ssr: false },
)

const PieChartVisualization = dynamic(
  () =>
    import("./visualizations/PieChartVisualization").then(
      (mod) => mod.PieChartVisualization,
    ),
  { ssr: false },
)

const HistogramVisualization = dynamic(
  () =>
    import("./visualizations/HistogramVisualization").then(
      (mod) => mod.HistogramVisualization,
    ),
  { ssr: false },
)

const TopListVisualization = dynamic(
  () =>
    import("./visualizations/TopListVisualization").then(
      (mod) => mod.TopListVisualization,
    ),
  { ssr: false },
)

const TextVisualization = dynamic(
  () =>
    import("./visualizations/TextVisualization").then(
      (mod) => mod.TextVisualization,
    ),
  { ssr: false },
)

// Chart visualization registry
const CHART_VISUALIZATIONS = {
  line: LineChartVisualization,
  bar: BarChartVisualization,
  area: AreaChartVisualization,
  statistic: StatisticVisualization,
  pie: PieChartVisualization,
  histogram: HistogramVisualization,
  "top-list": TopListVisualization,
  text: TextVisualization,
} as const

/** Visualizations drawn as a plot, which get a name, summary and table view. */
const FIGURE_VISUALIZATION_TYPES: ReadonlySet<string> = new Set([
  VISUALIZATION_TYPE.LINE,
  VISUALIZATION_TYPE.BAR,
  VISUALIZATION_TYPE.AREA,
  VISUALIZATION_TYPE.PIE,
  VISUALIZATION_TYPE.HISTOGRAM,
  VISUALIZATION_TYPE.TOP_LIST,
])

const CHART_CLICK_DRAG_THRESHOLD_PX = 5
const CHART_CLICK_CONTROL_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  '[role="button"]',
  "[data-dashboard-chart-control]",
  ".recharts-legend-wrapper",
].join(",")

function isChartControlClickTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest(CHART_CLICK_CONTROL_SELECTOR))
  )
}

interface ChartWidgetContentProps {
  config: ChartWidgetConfig
  /** The widget's title, used as the chart's accessible name. */
  title: string
  onPointClick?: (point: ChartClickData) => void
  onChartClick?: (point: ChartClickData) => void
  onSplitKeysChange?: (splitKeys: string[]) => void
  onConfigChange?: (newConfig: ChartWidgetConfig) => void
  onExportDataReady?: (data: WidgetExportData | null) => void
  widgetDimensions?: WidgetDimensions
  chartSyncId: string
}

const ChartWidgetContent: React.FC<ChartWidgetContentProps> = (props) => {
  const widgetData = useChartWidgetData({
    config: props.config,
    onSplitKeysChange: props.onSplitKeysChange,
    onExportDataReady: props.onExportDataReady,
  })
  return (
    <div className="flex h-full min-h-0 flex-col">
      {widgetData.metricRefreshError &&
      !widgetData.metricDefinitionError &&
      !widgetData.metricFilterError ? (
        <Alert variant="warning">
          Metric refresh failed. Showing the last loaded definitions.{" "}
          <Button variant="secondary" onClick={widgetData.recheckMetricCatalog}>
            Retry
          </Button>
        </Alert>
      ) : null}
      <div className="min-h-0 flex-1">
        <ChartWidgetBody {...props} widgetData={widgetData} />
      </div>
    </div>
  )
}

const ChartWidgetBody: React.FC<
  ChartWidgetContentProps & {
    widgetData: ReturnType<typeof useChartWidgetData>
  }
> = ({
  config,
  title,
  onChartClick,
  onPointClick,
  onConfigChange,
  widgetDimensions,
  chartSyncId,
  widgetData,
}) => {
  const parentToggleSeries = useContext(SeriesVisibilityContext)
  const [viewerHiddenSeries, setViewerHiddenSeries] = React.useState<
    string[] | null
  >(null)
  const viewerActions = React.useMemo(
    () => ({
      toggleSeries: (key: string) =>
        setViewerHiddenSeries((previous) =>
          toggleHiddenSeries(previous ?? config.hiddenSeries, key),
        ),
      isolateSeries: (key: string, allKeys: readonly string[]) =>
        setViewerHiddenSeries((previous) =>
          isolateSeries(previous ?? config.hiddenSeries, key, allKeys),
        ),
    }),
    [config.hiddenSeries],
  )
  const chartClickStartRef = React.useRef<{
    clientX: number
    clientY: number
  } | null>(null)
  const suppressChartClickRef = React.useRef(false)
  const {
    chartData,
    data,
    error,
    handleChartZoom,
    isLoading,
    isMetricLoading,
    isMetricMissing,
    lookups,
    lookupsLoading,
    metric,
    metricCatalogError,
    metricFilterError,
    metricDefinitionError,
    resolvedConfig,
    recheckMetricCatalog,
    showSuccessCriteriaError,
    splitKeys,
    stableDateRange,
    baselineData,
    statisticData,
  } = widgetData

  const visualizationConfig = React.useMemo(
    () =>
      parentToggleSeries || viewerHiddenSeries === null
        ? resolvedConfig
        : { ...resolvedConfig, hiddenSeries: viewerHiddenSeries },
    [resolvedConfig, parentToggleSeries, viewerHiddenSeries],
  )

  const chartLevelClickData = React.useMemo(
    () =>
      buildChartLevelDrillDownClickData({
        config: resolvedConfig,
        dateRange: stableDateRange,
      }),
    [resolvedConfig, stableDateRange],
  )

  const handleScopedPointClick = React.useCallback(
    (point: ChartClickData) => {
      suppressChartClickRef.current = true
      globalThis.setTimeout(() => {
        suppressChartClickRef.current = false
      }, 0)
      const metadataGroupKey = point.splitValue
        ? (parseSplitKey(point.splitValue).groupId ?? point.splitValue)
        : undefined
      const metadataGroup = metadataGroupKey
        ? parseMetadataGroupKey(metadataGroupKey)
        : null
      const drillDownMetadataGroup =
        config.groupByMetadataKey &&
        metadataGroup &&
        metadataGroup.value !== null
          ? {
              kind: metadataGroup.kind,
              topValues: metadataGroup.topValues,
              value: metadataGroup.value,
            }
          : null

      onPointClick?.({
        ...point,
        drillDownScope: point.drillDownScope ?? "point",
        ...(config.groupByMetadataKey && drillDownMetadataGroup
          ? {
              groupByMetadataKey: config.groupByMetadataKey,
              metadataGroupKind: drillDownMetadataGroup.kind,
              metadataTopValues: drillDownMetadataGroup.topValues,
              splitBy: undefined,
              splitValue: drillDownMetadataGroup.value,
            }
          : {}),
      })
    },
    [config.groupByMetadataKey, onPointClick],
  )

  const handleScopedChartClick = React.useCallback(
    (point: ChartClickData) => {
      suppressChartClickRef.current = true
      globalThis.setTimeout(() => {
        suppressChartClickRef.current = false
      }, 0)
      onChartClick?.(point)
    },
    [onChartClick],
  )

  const handleChartPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        chartClickStartRef.current = null
        return
      }

      chartClickStartRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      }
    },
    [],
  )

  const handleChartSurfaceClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onChartClick || !chartLevelClickData || event.defaultPrevented) {
        return
      }

      if (isChartControlClickTarget(event.target)) {
        return
      }

      if (suppressChartClickRef.current) {
        suppressChartClickRef.current = false
        return
      }

      const clickStart = chartClickStartRef.current
      chartClickStartRef.current = null
      if (clickStart) {
        const distance = Math.hypot(
          event.clientX - clickStart.clientX,
          event.clientY - clickStart.clientY,
        )
        if (distance > CHART_CLICK_DRAG_THRESHOLD_PX) {
          return
        }
      }

      onChartClick(chartLevelClickData)
    },
    [chartLevelClickData, onChartClick],
  )

  if (!metric && metricCatalogError) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          <p>Couldn't load this widget's metric</p>
          <Button
            variant="secondary"
            className="mt-2"
            onClick={recheckMetricCatalog}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // A missing metric also leaves no success criteria, so it is named first.
  if (isMetricMissing) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          <p>This widget's metric can't be found</p>
          <p className="text-xs mt-1">
            If it was just created, check again. Otherwise edit the widget to
            pick a metric.
          </p>
          <Button
            variant="secondary"
            className="mt-2"
            onClick={recheckMetricCatalog}
          >
            Check again
          </Button>
        </div>
      </div>
    )
  }

  if (showSuccessCriteriaError) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <ListChecks className="w-8 h-8 mx-auto mb-2" />
          <p>Success criteria is required for success rate</p>
        </div>
      </div>
    )
  }

  if (isLoading || lookupsLoading || isMetricLoading) {
    return <ChartWidgetSkeleton />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          <p>
            {metricDefinitionError || metricFilterError || "Error loading data"}
          </p>
          {(metricDefinitionError || metricFilterError) && (
            <Button
              variant="secondary"
              className="mt-2"
              onClick={recheckMetricCatalog}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return <WidgetNoDataState dateRange={stableDateRange} />
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <BarChart3 className="w-8 h-8 mx-auto mb-2" />
          <p>This chart can't plot the returned data</p>
        </div>
      </div>
    )
  }

  const VisualizationComponent = CHART_VISUALIZATIONS[config.visualizationType]

  if (!VisualizationComponent) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2" />
          <p>Unsupported visualization type: {config.visualizationType}</p>
        </div>
      </div>
    )
  }

  const visualization = (
    <div
      role="presentation"
      className="relative h-full w-full"
      onPointerDown={handleChartPointerDown}
      onClick={handleChartSurfaceClick}
    >
      {/* The keyboard path to the drill-down a click on the chart opens. */}
      {onChartClick && chartLevelClickData && (
        <Button
          variant="secondary"
          className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:left-2 focus-visible:top-2 focus-visible:z-10"
          // Neither half of the click reaches the chart surface, so it never
          // records a drag start this button would leave behind.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation()
            onChartClick(chartLevelClickData)
          }}
        >
          Open results in this range
        </Button>
      )}
      <VisualizationComponent
        metric={metric}
        chartData={chartData}
        config={visualizationConfig}
        splitKeys={splitKeys}
        colors={
          config.visualizationType === VISUALIZATION_TYPE.LINE
            ? lineSeriesColors(splitKeys?.length || 8)
            : chartSeriesColors(splitKeys?.length || 8)
        }
        onPointClick={onPointClick ? handleScopedPointClick : undefined}
        onChartClick={handleScopedChartClick}
        widgetDimensions={widgetDimensions}
        displayNameLookups={lookups}
        onConfigChange={onConfigChange}
        statisticData={statisticData}
        baselineData={config.showBaselineBands ? baselineData : null}
        dateRange={stableDateRange}
        onZoom={handleChartZoom}
        chartSyncId={chartSyncId}
      />
    </div>
  )
  const chart = FIGURE_VISUALIZATION_TYPES.has(config.visualizationType) ? (
    <ChartWidgetFigure
      label={title}
      config={visualizationConfig}
      chartData={chartData}
      splitKeys={splitKeys}
      displayNameLookups={lookups}
      dateRange={stableDateRange}
      onPointClick={onPointClick ? handleScopedPointClick : undefined}
    >
      {visualization}
    </ChartWidgetFigure>
  ) : (
    visualization
  )

  if (parentToggleSeries) {
    return chart
  }
  return (
    <SeriesVisibilityContext.Provider value={viewerActions.toggleSeries}>
      <SeriesIsolationContext.Provider value={viewerActions.isolateSeries}>
        {chart}
      </SeriesIsolationContext.Provider>
    </SeriesVisibilityContext.Provider>
  )
}

export class ChartWidget extends BaseWidget {
  type = "chart"
  title = "Metric Chart"
  description = "Dynamic metric visualization"
  icon = (<TrendingUp className="w-5 h-5" />)
  disabled = false

  renderWidget(
    props: WidgetProps & {
      onPointClick?: (point: ChartClickData) => void
      onChartClick?: (point: ChartClickData) => void
      onSplitKeysChange?: (splitKeys: string[]) => void
    },
  ): React.ReactNode {
    const config = props.widget.config as ChartWidgetConfig
    const { onChartClick, onPointClick, onSplitKeysChange, onExportDataReady } =
      props

    // Calculate widget dimensions from grid properties
    const gridW = props.widget.grid_w || 12
    const gridH = props.widget.grid_h || 4
    const containerWidth = getResponsiveContainerWidth()
    const scaledDimensions = getScaledDimensions(containerWidth)

    // Calculate pixel dimensions
    const pixelW = (containerWidth / GRID_COLUMNS) * gridW
    const pixelH = scaledDimensions.rowHeight * gridH

    const widgetDimensions: WidgetDimensions = {
      gridW,
      gridH,
      pixelW,
      pixelH,
    }

    if (!config?.metricId || !config?.visualizationType) {
      return (
        <div className="flex items-center justify-center h-full text-coval-text-muted">
          <div className="text-center">
            <TrendingUp className="w-8 h-8 mx-auto mb-2" />
            <p>Configure metric and visualization</p>
          </div>
        </div>
      )
    }

    return (
      <div className="w-full h-full select-none">
        <ChartWidgetContent
          config={config}
          title={config.title || props.widget.title || "Chart"}
          onChartClick={onChartClick}
          onPointClick={onPointClick}
          onSplitKeysChange={onSplitKeysChange}
          onConfigChange={props.widget?.onConfigChange}
          onExportDataReady={onExportDataReady}
          widgetDimensions={widgetDimensions}
          chartSyncId={getDashboardWidgetChartSyncId(props.widget.id)}
        />
      </div>
    )
  }

  getDefaultConfig(): ChartWidgetConfig {
    return {
      title: this.title,
      description: "",
      metricId: "",
      dataSource: DATA_SOURCE.SIMULATED,
      aggregation: AGGREGATION_TYPE.AVG,
      visualizationType: VISUALIZATION_TYPE.LINE,
      bucketInterval: BUCKET_INTERVAL.FOUR_HOURS,
      stacked: false,
      grouped: false,
      showAsPercentage: false,
      splitBy: undefined,
      metricOutputType: "float",
      filters: {},
      xAxisLabel: undefined,
      yAxisLabel: undefined,
    }
  }
}
